
import _ from "lodash";
import rest from "@/rest";
import { Component, Vue, Watch, Prop, PropSync } from "vue-property-decorator";
import { Action, namespace } from "vuex-class";
import { List } from "linq-collections";
import { vuex } from "@/store";
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
// component
// interfaces
import { ExBundleVM } from "@/interfaces/ExBundleAssignmentGroupVM";
import L3FrameWithTitle from "./L3FrameWithTitle.vue";
import { CustomizationVm } from "@/interfaces/CustomizationVm";
import { ParamDictionary } from "@/interfaces/ParamDictionary";

//const auth = namespace("auth");
const globals = namespace("globals");
const customization = namespace("customization");

@Component({
  components: {
    L3FrameWithTitle
  }
})
export default class ImageAiDlg extends Vue {
  @PropSync("showDialog") syncedShowDialog!: boolean;
  @PropSync("imageSrc") syncedImage!: string | null;
  @Prop() relativePath!: string; // optional relative path to subdir of bundle dir
  @PropSync("prompt") syncedPrompt!: string | null;
  @PropSync("changePrompt") syncedChangePrompt!: string | null;
  @Prop({ default: true }) enableAltText!: boolean;
  @PropSync("altText") syncedAltText!: string | null;
  @PropSync("aspectRatio") syncedAspectRatio!: string | null;
  @PropSync("imageStyle") syncedImageStyle!: string | null;

  @customization.Getter customizationSettings!: CustomizationVm;
  @globals.Getter selectedBundle: any;
  @globals.Getter selectedLanguageCode!: string | null;
  // @auth.Getter isAdmin: any;
  // @auth.Getter isOrgAdmin: any;
  // @auth.Getter isGroupAdmin: any;

  isBusyOnCreateAltText = false;
  isBusyOnAdjustImage = false;
  isBusyOnCreateImage = false;
  busyOnSave = false;
  editPrompt = "";
  editChangePrompt = "";
  editImageSrc = "";
  editAltText = "";
  editAspectRatio = "";
  editImageStyle = "";


  // $refs!: {
  //   signatureComponent: any;
  // };

  mounted() {
  }

  @Watch("syncedShowDialog")
  async onShowDialogChanged(val: boolean) {
    if (this.syncedShowDialog) {
      // On dialog open
      await this.updateView();
      // Local copy of props to edit in dialog without directly changing the prop value until saving
      this.editPrompt = this.syncedPrompt ?? "";
      this.editChangePrompt = this.syncedChangePrompt ?? "";
      this.editImageSrc = this.syncedImage ?? "";
      this.editAltText = this.syncedAltText ?? "";
      this.editAspectRatio = this.syncedAspectRatio ?? "1:1";
      this.editImageStyle = this.syncedImageStyle ?? "Photo realistic";

      // Disable body scroll on iOS
      this.$nextTick(async () => {
        await this.$globalHelper.delay(10);
        const modal = document.querySelector('.modal');
        if (modal) {
          disableBodyScroll(modal);
        }
      });
    } else {
      // Dialog closing
      // Only clear scroll locks if they exist
      if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        const modal = document.querySelector('.modal');
        if (modal) {
          clearAllBodyScrollLocks();
        }
      }
    }
  }

  beforeDestroy() {
    // Only clear scroll locks if they exist
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      const modal = document.querySelector('.modal');
      if (modal) {
        clearAllBodyScrollLocks();
      }
    }
  }

  async updateView() {
  }

  private async createAltText(): Promise<void> {
    if (!this.selectedBundle) return;

    this.isBusyOnCreateAltText = true;
    let params: ParamDictionary = { dictionary: {}};
    params.dictionary!["BundleId"] = this.selectedBundle?.id?.toString() ?? "0";
    params.dictionary!["FilePath"] = this.editImageSrc ?? "";
    params.dictionary!["RelativePath"] = this.relativePath || "";
    params.dictionary!["LanguageCode"] = this.selectedLanguageCode || "de-DE";

    rest.url("contentManager/createImageAltText").post(params)
      .then((response) => {
        console.log("response", response);
        if (response) {
          this.editAltText = response;
        }
      })
      .catch(err => {
      })
      .finally(() => {
        this.isBusyOnCreateAltText = false;
      });
  }

  private async createImage(): Promise<void> {
    if (!this.selectedBundle) return;

    this.isBusyOnCreateImage = true;
    let params: ParamDictionary = { dictionary: {}};
    params.dictionary!["BundleId"] = this.selectedBundle?.id?.toString() ?? "0";
    params.dictionary!["FilePath"] = this.editImageSrc ?? "";
    params.dictionary!["RelativePath"] = this.relativePath || "";
    params.dictionary!["Prompt"] = this.editPrompt ?? "";
    params.dictionary!["ChangePrompt"] = "";
    params.dictionary!["AspectRatio"] = this.editAspectRatio ?? "1:1";
    params.dictionary!['Style'] = this.editImageStyle ?? "Pictogram";
    const accent = (this.$vuetify.theme.themes as any)["light"]?.accent ?? (this.$store.state.ux?.colorAccent ?? "#006064");
    params.dictionary!["AccentColor"] = accent;

    try {
      const response = await rest.url("contentManager/createImage").post(params);
      if (response) {
        this.editImageSrc = response;
        this.editAltText = ""; // reset alt text when creating new image
      }
      this.$root.$emit('fileListUpdate:MediaPool'); // notify media pool via global event to refresh file list
    }
    catch (err) {
      // swallow — caller may handle messaging
      console.error('createImage error', err);
    }
    finally {
      this.isBusyOnCreateImage = false;
    }
  }

  private async adjustImage(): Promise<void> {
    this.isBusyOnAdjustImage = true;

    if (!this.selectedBundle) return;
    let params: ParamDictionary = { dictionary: {}};
    params.dictionary!["BundleId"] = this.selectedBundle?.id?.toString() ?? "0";
    params.dictionary!["FilePath"] = this.editImageSrc ?? "";
    params.dictionary!["RelativePath"] = this.relativePath || "";
    params.dictionary!["Prompt"] = "";
    params.dictionary!["ChangePrompt"] = this.editChangePrompt ?? "";
    params.dictionary!["AspectRatio"] = this.editAspectRatio ?? "1:1";
    params.dictionary!["Style"] = this.editImageStyle ?? "Pictogram";
    const accent = (this.$vuetify.theme.themes as any)["light"]?.accent ?? (this.$store.state.ux?.colorAccent ?? "#006064");
    params.dictionary!["AccentColor"] = accent;
    try {
      const response = await rest.url("contentManager/createImage").post(params);
      if (response) {
        this.editImageSrc = response;
        this.editAltText = ""; // reset alt text when creating new image
      }
      this.$root.$emit('fileListUpdate:MediaPool');
    }
    catch (err) {
      console.error('adjustImage error', err);
    }
    finally {
      this.isBusyOnAdjustImage = false;
    }
  }

  get disableSaveButton() {
    return this.isBusyOnAdjustImage || this.isBusyOnCreateImage || this.isBusyOnCreateAltText;
  }

  onSave() {
    this.syncedPrompt = this.editPrompt;
    this.syncedChangePrompt = this.editChangePrompt;
    this.syncedImage = this.editImageSrc;
    this.syncedAltText = this.editAltText;
    this.syncedAspectRatio = this.editAspectRatio;
    this.syncedImageStyle = this.editImageStyle;
    this.syncedShowDialog = false;
  }

  onCancel() {
    clearAllBodyScrollLocks();
    this.syncedShowDialog = false;
  }
}
